import {useState} from 'react'
import TableBody from '@mui/material/TableBody'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {
  StyledAnchor,
  StyledArrowForwardIcon,
  StyledPaper,
  StyledProjectListTable,
  StyledTableContainer,
  StyledTablePagination,
  StyledWarningAmberOutlinedIcon
} from './ProjectListLandingStyles'
import {useNavigate} from 'react-router-dom'
import PropTypes from 'prop-types'
import {StyledTypography, StyledTableCell, StyledTableCellHeader} from '../AdminPanelStyling'
import {IconButton} from '@mui/material'
import {useRolePermissions} from '../../hooks/useRolesPermissions'

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25]

const ProjectListItems = ({data, clientId, clientName}) => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0])

  const {isCustomerSupport, isSuperAdmin} = useRolePermissions()

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const ariaLabelProjectListTable = `Table of projects within ${clientName}, ${data.length} projects total`

  const handleViewProject = (event, projectId) => {
    event.preventDefault()
    navigate(`/adminpanel/client/${clientId}/project/${projectId}`, {
      state: {clientName, projectData: data}
    })
  }

  return (
    <StyledPaper elevation={0}>
      <StyledTypography variant='subtitle1' fontWeight={500}>
        All projects in this client group
      </StyledTypography>

      <StyledTableContainer>
        <StyledProjectListTable tabIndex={0} aria-label={ariaLabelProjectListTable} stickyHeader>
          <TableHead>
            {data.length > 0 && (
              <TableRow>
                <StyledTableCell tabIndex={0} aria-label='name'>
                  <StyledTableCellHeader>Project Name</StyledTableCellHeader>
                </StyledTableCell>
                <StyledTableCell align='right' tabIndex={0} aria-label='total-members'>
                  <StyledTableCellHeader>Active Members</StyledTableCellHeader>
                </StyledTableCell>
                {(isSuperAdmin() || isCustomerSupport()) && (
                  <StyledTableCell align='right' tabIndex={0} aria-label='total-members'>
                    <StyledTableCellHeader>Actions</StyledTableCellHeader>
                  </StyledTableCell>
                )}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.GROUP_ID}>
                <StyledTableCell component='th' scope='row' align='left'>
                  <StyledWarningAmberOutlinedIcon alt='Incomplete project' isCompleted={row.IS_COMPLETED} />
                  <StyledAnchor
                    aria-label={row.IS_COMPLETED ? `${row.GROUP_NAME}-link` : `incomplete-${row.GROUP_NAME}-link`}
                    href='#'
                    onClick={(event) => {
                      event.preventDefault()
                      navigate(`/adminpanel/client/${clientId}/project/${row.GROUP_ID}`, {
                        state: {clientName, isCompleted: row.IS_COMPLETED, projectData: data}
                      })
                    }}
                  >
                    {row.GROUP_NAME.replaceAll('_', ' ')}
                  </StyledAnchor>
                </StyledTableCell>
                <StyledTableCell align='right' tabIndex={0} aria-label={`${row.TOTAL_ACTIVE_USERS}-total-members`}>
                  {row.TOTAL_ACTIVE_USERS}
                </StyledTableCell>
                {(isSuperAdmin() || isCustomerSupport()) && (
                  <StyledTableCell align='right'>
                    <IconButton
                      onClick={(event) => handleViewProject(event, row.GROUP_ID)}
                      color='primary'
                      size='small'
                      aria-label={`View project details for ${row.GROUP_NAME.replaceAll('_', ' ')}`}
                    >
                      <StyledArrowForwardIcon />
                    </IconButton>
                  </StyledTableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </StyledProjectListTable>
        {data.length > 0 && (
          <StyledTablePagination
            component='div'
            count={data.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            showFirstButton
            showLastButton
          />
        )}
      </StyledTableContainer>
    </StyledPaper>
  )
}

ProjectListItems.propTypes = {
  data: PropTypes.array.isRequired,
  clientId: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired
}

export default ProjectListItems
